import { AxiosError } from 'axios';
import { api } from '@/Data/api';
import ss from '../Session';
import { uploadImageToS3 } from '@/Data/Image';
import type { ApplicationDocument } from 'ai-api/types/admission';
import type { AdmissionApiResponse } from './index';
import { getStoredLanguage } from '@/Components/LanguageSelector';

export interface DocumentSubmissionParams {
    applicationid: string;
    documentid: string;
    userid: string;
    file?: File | string;
    note?: string;
}

export interface DocumentValidationParams {
    applicationid: string;
    documentid: string;
    status: 'approved' | 'rejected' | 'revision_needed';
    validator_notes?: string;
}

export interface DocumentListParams {
    applicationid: string;
    documentid?: string;
    addressid?: string;
}

/**
 * Upload file and submit application document
 * @param params DocumentSubmissionParams with file data
 * @returns Promise with success status or null
 */
export const submitApplicationDocumentAPI = async ({ applicationid, documentid, userid, file, note }: DocumentSubmissionParams): Promise<boolean> => {
    try {

        if (!applicationid || !documentid || !userid) {
            throw new Error('Application ID, Document ID, and User ID are required');
        }

        let file_url: string | undefined;
        let file_name: string | undefined;

        // Handle file upload if provided
        if (file) {
            if (file instanceof File) {
                // Upload File object to S3
                const blob = new Blob([file], { type: file.type });
                const objectURL = URL.createObjectURL(blob);
                
                try {
                    file_url = await uploadImageToS3(objectURL, file.name) ?? undefined;
                    file_name = file.name;
                    
                    // Clean up object URL
                    URL.revokeObjectURL(objectURL);
                } catch (uploadError) {
                    URL.revokeObjectURL(objectURL);
                    throw new Error('File upload failed');
                }
            } else if (typeof file === 'string') {
                // Handle string URL (for existing files)
                const uploadedUrl = await uploadImageToS3(file);
                if (uploadedUrl) {
                    file_url = uploadedUrl;
                    file_name = file.split('/').pop() || `document_${Date.now()}`;
                } else {
                    throw new Error('File upload failed');
                }
            }

            if (!file_url) {
                throw new Error('File upload failed');
            }
        }

        // Submit document to admission API
        const res = await api.put<AdmissionApiResponse<{data: number, message?: string}>>(
            '/admission/application-document',
            {
                applicationid,
                documentid,
                userid,
                file_url,
                file_name,
                note
            },
            {
                headers: {
                    Authorization: `Bearer ${ss.getCurrentSession().jwt}`,
                    'Accept-Language': getStoredLanguage()
                }
            }
        );

        return res.data?.code === 200;
    } catch (e) {
        console.error('Failed to submit application document:', e);
        return false;
    }
};

/**
 * Validate application document (manager only)
 * @param params DocumentValidationParams
 * @returns Promise with success status or null
 */
export const validateApplicationDocumentAPI = async ({ applicationid, documentid, status, validator_notes }: DocumentValidationParams): Promise<boolean> => {
    try {

        if (!applicationid || !documentid || !status) {
            throw new Error('Application ID, Document ID, and Status are required');
        }

        const res = await api.put<AdmissionApiResponse<{code: number, message?: string}>>(
            '/admission/application-document',
            {
                applicationid,
                documentid,
                status,
                validator_notes
            },
            {
                headers: {
                    Authorization: `Bearer ${ss.getCurrentSession().jwt}`,
                    'Accept-Language': getStoredLanguage()
                }
            }
        );

        return res.data?.code === 200;
    } catch (e) {
        console.error('Failed to validate application document:', e);
        return false;
    }
};

/**
 * List application documents
 * @param params DocumentListParams
 * @returns Promise with application documents array or null
 */
export const listApplicationDocumentsAPI = async ({ applicationid, documentid, addressid }: DocumentListParams): Promise<ApplicationDocument[] | null> => {
    try {

        if (!applicationid) {
            throw new Error('Application ID is required');
        }

        const res = await api.get<AdmissionApiResponse<ApplicationDocument[]>>(
            '/admission/application-document',
            {
                params: {
                    applicationid,
                    documentid,
                    addressid
                },
                headers: {
                    Authorization: `Bearer ${ss.getCurrentSession().jwt}`,
                    'Accept-Language': getStoredLanguage()
                }
            }
        );

        if (res.data?.code === 200) {
            return res.data.data || [];
        }

        return null;
    } catch (e) {
        if (e instanceof AxiosError && e.response?.status === 404) {
            console.warn('Application documents not found:', { applicationid, documentid, addressid });
            return [];
        }
        console.error('Failed to list application documents:', e);
        return null;
    }
};

/**
 * Delete application document
 * @param applicationid Application ID
 * @param documentid Document ID
 * @returns Promise with success status
 */
export const deleteApplicationDocumentAPI = async (applicationid: string, documentid: string): Promise<boolean> => {
    try {
        if (!applicationid || !documentid) {
            throw new Error('Application ID and Document ID are required');
        }

        const res = await api.delete<AdmissionApiResponse<any>>(
            '/admission/application-document',
            {
                params: {
                    applicationid,
                    documentid
                },
                headers: {
                    Authorization: `Bearer ${ss.getCurrentSession().jwt}`,
                    'Accept-Language': getStoredLanguage()
                }
            }
        );

        return res.data?.code === 200;
    } catch (e) {
        console.error('Failed to delete application document:', e);
        return false;
    }
};